import React, { useEffect, useMemo, useState } from "react";
import { Check, Copy, CreditCard, Gift, Info, Loader2, Lock, ShieldCheck } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ApiService } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { getPaymentProviderMeta } from "../../utils/paymentProviders";
import { buildInviteLink, buildWhatsAppShareUrl, readRememberedReferralCode, rememberReferralCode } from "../../utils/referral";
import OnBilgilendirmePenceresi from "./OnBilgilendirmePenceresi";

const CARD_LOGOS = [
  { name: "Mastercard", src: "/images/payment-providers/mastercard.svg", className: "max-h-8" },
  { name: "Visa", src: "/images/payment-providers/visa.svg", className: "max-h-7" },
  { name: "Troy", src: "/images/payment-providers/troy.svg", className: "max-h-8" },
  { name: "BKM Express", src: "/images/payment-providers/bkm-express.png", className: "max-h-8" },
  { name: "Discover", src: "/images/payment-providers/discover.svg", className: "max-h-7" },
  { name: "American Express", src: "/images/payment-providers/american-express.svg", className: "max-h-8" },
];

type FormState = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  // Fatura bilgileri
  billingType: "individual" | "corporate";
  identityNumber: string;
  companyName: string;
  taxOffice: string;
  taxNumber: string;
  address: string;
  district: string;
  city: string;
};

type FormErrors = Partial<Record<keyof FormState, string>>;

function validateForm(form: FormState): FormErrors {
  const errors: FormErrors = {};
  if (!form.firstName.trim()) errors.firstName = "Ad zorunludur.";
  if (!form.lastName.trim()) errors.lastName = "Soyad zorunludur.";
  if (!form.email.trim()) {
    errors.email = "E-posta zorunludur.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    errors.email = "Geçerli bir e-posta adresi girin.";
  }
  if (!form.phone.trim()) {
    errors.phone = "Telefon zorunludur.";
  } else if (!/^(\+90|0)?\d{10}$/.test(form.phone.replace(/\s/g, ""))) {
    errors.phone = "Geçerli bir telefon numarası girin.";
  }
  if (!form.address.trim()) errors.address = "Fatura adresi zorunludur.";
  if (!form.district.trim()) errors.district = "İlçe zorunludur.";
  if (!form.city.trim()) errors.city = "İl zorunludur.";
  if (form.billingType === "corporate") {
    if (!form.companyName.trim()) errors.companyName = "Firma unvanı zorunludur.";
    if (!form.taxOffice.trim()) errors.taxOffice = "Vergi dairesi zorunludur.";
    if (!/^\d{10}$/.test(form.taxNumber.trim())) errors.taxNumber = "Vergi numarası 10 haneli olmalıdır.";
  } else if (!form.identityNumber.trim()) {
    errors.identityNumber = "TC kimlik numarası zorunludur.";
  } else if (!/^\d{11}$/.test(form.identityNumber.trim())) {
    errors.identityNumber = "TC kimlik numarası 11 haneli olmalıdır.";
  }
  return errors;
}

const REQUIRED_FIELDS: (keyof FormState)[] = [
  "firstName", "lastName", "email", "phone", "address", "district", "city",
  "identityNumber", "companyName", "taxOffice", "taxNumber",
];

// Ödeme sayfasında paketler panelde verilen "Sayfadaki başlık"a göre, panel sırasıyla gruplanır.
function groupPackages(packages: any[]): Array<{ title: string; items: any[] }> {
  const groups: Array<{ title: string; items: any[] }> = [];
  for (const item of packages) {
    const title = String(item.group || "").trim();
    const group = groups.find((other) => other.title === title);
    if (group) group.items.push(item);
    else groups.push({ title, items: [item] });
  }
  return groups;
}

// Açılışta seçili paket: kilitli bir bölümden gelindiyse o bölümü açan paket, yoksa önerilen,
// yoksa ilk paket. Zaten sahip olunan (sabit tarihli) paket seçili gelmez.
function pickInitialPackage(packages: any[], moduleId: string | null): string {
  const buyable = packages.filter((item) => !item.alreadyOwned);
  const pool = buyable.length ? buyable : packages;
  const opening = moduleId ? pool.filter((item) => (item.opens || []).some((open: any) => open.id === moduleId)) : [];
  const candidates = opening.length ? opening : pool;
  return (candidates.find((item) => item.recommended) || candidates[0])?.id || "";
}

const formatTry = (value: number) =>
  Number(value || 0).toLocaleString("tr-TR", { style: "currency", currency: "TRY" });

// Paketin taksit vaadi (panelden yönetilir). interestFree = vade farkını işyeri üstleniyor:
// müşteri taksitte de paket fiyatını öder. Taksit sayısı iyzico'nun güvenli sayfasında seçilir.
// amount: indirim varsa ödenecek tutar (taksit tutarı ona göre).
function getInstallmentOffer(pkg: any, amount?: number): { max: number; interestFree: boolean; perInstallment: number } | null {
  const max = Number(pkg?.maxInstallment || 1);
  if (max <= 1) return null;
  return {
    max,
    interestFree: pkg.installmentFeeBearer === "merchant",
    perInstallment: Number(amount ?? pkg.price ?? 0) / max,
  };
}

function formatEndsAt(iso?: string): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function formatDay(iso?: string | null): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
}

// Deneme takvimindeki gün: "7 Kasım Cumartesi (TYT)".
function formatExamDay(day: { date: string; label?: string }): string {
  const text = new Date(`${day.date}T12:00:00Z`).toLocaleDateString("tr-TR", { day: "numeric", month: "long", weekday: "long", timeZone: "UTC" });
  return day.label ? `${text} (${day.label})` : text;
}

// Paketin bitişi: günlük paket saatiyle (ertesi sabah 07:30), diğerleri yalnız gün olarak. Saat,
// sayfanın açıldığı dakikadan geldiği için gösterilmez (yanıltıyordu).
function formatPackageEnd(pkg: any): string {
  if (!pkg?.endsAtPreview) return "";
  return pkg.expiresNextDayQrOpen ? formatEndsAt(pkg.endsAtPreview) : formatDay(pkg.endsAtPreview);
}

export default function PaymentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo") || "/dashboard";
  // Kilitli bir bölümden gelindiyse (menüdeki kilit) o bölüm: açan paket seçili gelir.
  const lockedModuleId = searchParams.get("module");
  // Davet linki ödeme sayfasına da gelebilir (/payment?davet=YES-XXXXX).
  const urlReferralCode = (searchParams.get("davet") || "").trim().toUpperCase();
  const { user } = useAuth();

  // Davet kodu: alana yazılan, uygulanan ve sunucunun hesapladığı indirim özeti (köprü dahil).
  const [referralInput, setReferralInput] = useState("");
  const [appliedReferralCode, setAppliedReferralCode] = useState("");
  const [discounts, setDiscounts] = useState<any>(null);
  const [checkingDiscounts, setCheckingDiscounts] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const [catalog, setCatalog] = useState<any>(null);
  const [packageId, setPackageId] = useState("");
  const [provider, setProvider] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState<FormState>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    billingType: "individual",
    identityNumber: "",
    companyName: "",
    taxOffice: "",
    taxNumber: "",
    address: "",
    district: "",
    city: "",
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Partial<Record<keyof FormState, boolean>>>({});
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  // Mesafeli satış ön bilgilendirme formu: ayrı onay (Yönetmelik md. 6); paket ya da tutar değişince yeniden onaylanır
  const [onBilgiAccepted, setOnBilgiAccepted] = useState(false);
  const [onBilgiAcik, setOnBilgiAcik] = useState(false);
  const [legalError, setLegalError] = useState("");

  // Hesap bilgileriyle ön-doldur, ardından daha önce kullanılmış fatura bilgisi varsa onunla güncelle.
  useEffect(() => {
    if (user) {
      setForm((prev) => ({
        ...prev,
        firstName: user.firstName || prev.firstName,
        lastName: user.lastName || prev.lastName,
        email: user.email || prev.email,
        phone: user.gsmNumber || prev.phone,
      }));
    }
  }, [user]);

  useEffect(() => {
    ApiService.getPaymentBillingProfile()
      .then((profile) => {
        if (!profile) return;
        setForm((prev) => ({
          ...prev,
          firstName: profile.firstName || prev.firstName,
          lastName: profile.lastName || prev.lastName,
          email: profile.email || prev.email,
          phone: profile.phone || prev.phone,
          billingType: profile.type === "corporate" ? "corporate" : prev.billingType,
          identityNumber: profile.identityNumber || prev.identityNumber,
          companyName: profile.companyName || prev.companyName,
          taxOffice: profile.taxOffice || prev.taxOffice,
          taxNumber: profile.taxNumber || prev.taxNumber,
          address: profile.address || prev.address,
          district: profile.district || prev.district,
          city: profile.city || prev.city,
        }));
      })
      .catch(() => { /* fatura profili yoksa form boş kalır */ });
  }, []);

  useEffect(() => {
    ApiService.getPaymentCatalog()
      .then((data) => {
        setCatalog(data);
        setPackageId(pickInitialPackage(data.packages || [], lockedModuleId));
        setProvider(data.providers?.[0]?.id || "");
        // Kod sırası: linkteki, kayıtta getirilen (kullanılmamış), tarayıcıda hatırlanan.
        const initialCode = urlReferralCode || data.referral?.invitedCode || readRememberedReferralCode();
        if (urlReferralCode) rememberReferralCode(urlReferralCode);
        if (initialCode && initialCode !== data.referral?.code) {
          setReferralInput(initialCode);
          setAppliedReferralCode(initialCode);
        }
      })
      .catch((err) => setError(err?.message || "Ödeme seçenekleri yüklenemedi."))
      .finally(() => setLoading(false));
  }, [lockedModuleId, urlReferralCode]);

  const selectedPackage = useMemo(
    () => catalog?.packages?.find((item: any) => item.id === packageId),
    [catalog, packageId]
  );

  const packageGroups = useMemo(() => groupPackages(catalog?.packages || []), [catalog]);
  const nextExamDay = catalog?.upcomingExamDays?.[0] || null;

  const lockedModule = useMemo(
    () => (user?.paymentAccess?.modules || []).find((m: any) => m.id === lockedModuleId && m.access !== "AUTHORIZED"),
    [user?.paymentAccess, lockedModuleId]
  );

  // Taksit yalnız iyzico'nun barındırdığı formda seçilebilir; diğer sağlayıcılar tek çekim gider —
  // o durumda taksit vaadi gösterilmez.
  // Davet indirimi ya da 30 gün köprüsü olan pakette tutar sunucuda hesaplanır (kişiye göre).
  const discountable = Boolean(selectedPackage && (Number(selectedPackage.referralDiscount) > 0 || selectedPackage.bridgeCredit));
  useEffect(() => {
    if (!discountable || !packageId) {
      setDiscounts(null);
      return;
    }
    let cancelled = false;
    setCheckingDiscounts(true);
    ApiService.checkPaymentReferral({
      packageId,
      code: Number(selectedPackage?.referralDiscount) > 0 ? appliedReferralCode || undefined : undefined,
    })
      .then((result) => { if (!cancelled) setDiscounts(result); })
      .catch(() => { if (!cancelled) setDiscounts(null); })
      .finally(() => { if (!cancelled) setCheckingDiscounts(false); });
    return () => { cancelled = true; };
  }, [discountable, packageId, appliedReferralCode, selectedPackage?.referralDiscount]);

  const payable = Number(discounts?.payable ?? selectedPackage?.price ?? 0);
  // Onaylanan ön bilgilendirme bu siparişe özeldir: paket ya da ödenecek tutar değişirse yeniden onaylanır
  useEffect(() => { setOnBilgiAccepted(false); }, [packageId, payable]);

  const installmentsSupported = provider === "iyzico";
  const selectedOffer = installmentsSupported ? getInstallmentOffer(selectedPackage, payable) : null;

  const inviteLink = catalog?.referral?.code ? buildInviteLink(catalog.referral.code) : "";
  const referralOffer = catalog?.referral?.offer || null;
  const inviteText = referralOffer
    ? `Yes Akademi'ye benim davetimle gel: ${referralOffer.packageLabel} alırsan ${formatTry(referralOffer.discount)} indirim kazanırsın. ${inviteLink}`
    : `Yes Akademi'ye benim davetimle gel: ${inviteLink}`;
  const copyInviteLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      window.prompt("Davet linkini kopyala:", inviteLink);
    }
  };

  const selectedProviderMeta = useMemo(
    () => (provider ? getPaymentProviderMeta(provider) : null),
    [provider]
  );

  const handleField = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (touched[field]) {
      const errors = validateForm({ ...form, [field]: value });
      setFormErrors((prev) => ({ ...prev, [field]: errors[field] }));
    }
  };

  const handleBlur = (field: keyof FormState) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const errors = validateForm(form);
    setFormErrors((prev) => ({ ...prev, [field]: errors[field] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(REQUIRED_FIELDS.reduce((acc, field) => ({ ...acc, [field]: true }), {}));
    const errors = validateForm(form);
    setFormErrors(errors);

    if (!onBilgiAccepted || !privacyAccepted || !termsAccepted) {
      setLegalError(!onBilgiAccepted
        ? "Devam etmek için Ön Bilgilendirme Formu'nu açıp onaylamanız gerekmektedir."
        : "Devam etmek için tüm sözleşmeleri kabul etmeniz gerekmektedir.");
      return;
    }
    setLegalError("");
    if (Object.keys(errors).length > 0) return;

    setError("");
    setSubmitting(true);
    try {
      const quoteResult = await ApiService.getPaymentQuote({
        packageId,
        provider,
        installment: 1,
        referralCode: discounts?.referral ? appliedReferralCode : undefined,
      });

      sessionStorage.setItem("paymentReturnTo", returnTo);
      const result = await ApiService.startPaymentCheckout({
        packageId,
        provider,
        quoteToken: quoteResult.quoteToken,
        yasal: { onBilgilendirme: true, sozlesmeler: true },
        billing: {
          type: form.billingType,
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: form.phone,
          identityNumber: form.identityNumber,
          companyName: form.companyName,
          taxOffice: form.taxOffice,
          taxNumber: form.taxNumber,
          address: form.address,
          district: form.district,
          city: form.city,
        },
      });

      if (result.paymentPageUrl) {
        window.location.assign(result.paymentPageUrl);
        return;
      }
      throw new Error("Ödeme sayfası bağlantısı alınamadı.");
    } catch (err: any) {
      setError(err?.message || "Ödeme başlatılamadı.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
      </div>
    );
  }

  if (!catalog?.paymentEnabled || !catalog?.packages?.length || !catalog?.providers?.length) {
    return (
      <div className="mx-auto mt-12 max-w-xl rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center">
        <Lock className="mx-auto mb-3 h-10 w-10 text-amber-600" />
        <h1 className="text-xl font-semibold text-slate-900">Ödeme Sistemi Şu Anda Kullanılamıyor</h1>
        <p className="mt-2 text-sm text-slate-600">Lütfen sistem yöneticinizle iletişime geçin.</p>
        <button
          onClick={() => navigate("/dashboard")}
          className="mt-5 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
        >
          Ana Sayfaya Dön
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-50 py-10 px-4 sm:px-8">
      <div className="mx-auto max-w-5xl">

        {/* Başlık */}
        <div className="mb-8 flex items-center gap-3">
          <div className="rounded-2xl bg-violet-100 p-2.5">
            <ShieldCheck className="h-6 w-6 text-violet-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">YES.Tools Paketler</h1>
            <p className="text-sm text-slate-500">İhtiyacına uygun paketi seç; her kartta paketin neyi açtığı yazar.</p>
          </div>
        </div>

        {lockedModule && (
          <div className="mb-4 flex items-start gap-3 rounded-2xl border border-violet-200 bg-violet-50 px-4 py-3.5 text-sm text-violet-900">
            <Lock className="mt-0.5 h-4 w-4 shrink-0 text-violet-600" />
            <p className="leading-relaxed">
              {lockedModule.unlockPackageCount === 1 && lockedModule.unlockPackageLabel
                ? <><strong>{lockedModule.label}</strong>, <strong>{lockedModule.unlockPackageLabel}</strong> ile açılır. Paketi senin için seçtik.</>
                : <><strong>{lockedModule.label}</strong> bölümünü açan bir paket seçili geldi; diğer seçenekler de aşağıda.</>}
            </p>
          </div>
        )}

        {catalog.currentAccess?.active && (
          <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3.5 text-sm text-emerald-800">
            <div className="flex items-start gap-3">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
              <div className="min-w-0 leading-relaxed">
                <p className="font-semibold">Aktif paketlerin</p>
                <ul className="mt-1 space-y-0.5">
                  {(catalog.currentAccess.items || []).map((item: any) => (
                    <li key={item.id}>
                      {item.label || "Paket"}: <strong>{formatDay(item.endsAt)}</strong> tarihine kadar
                      {item.opens?.length > 0 && <span className="text-emerald-700"> · {item.opens.map((open: any) => open.label).join(", ")}</span>}
                      {item.entries && <span className="text-emerald-700"> · {item.entries.remaining}/{item.entries.total} giriş kaldı</span>}
                    </li>
                  ))}
                </ul>
                <p className="mt-1 text-xs text-emerald-700">Aynı türden paket alırsan süre mevcut paketinin üzerine eklenir; hakkın kaybolmaz.</p>
              </div>
            </div>
          </div>
        )}

        {referralOffer && inviteLink && (
          <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3.5 text-sm text-amber-900">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
              <div className="flex min-w-0 flex-1 items-start gap-3">
              <Gift className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <div className="min-w-0 flex-1 leading-relaxed">
                <p className="font-semibold">Arkadaşını getir</p>
                <p>
                  Arkadaşın davet linkinle kayıt olup {referralOffer.packageLabel} alırsa
                  {referralOffer.reward ? <> <strong>{referralOffer.reward}</strong> senin,</> : ""} arkadaşına <strong>{formatTry(referralOffer.discount)}</strong> indirim.
                  Ödül, arkadaşının ödemesi kesinleşip 14 günlük cayma süresi dolunca verilir.
                </p>
                <p className="mt-1 text-xs">
                  Davet kodun: <strong className="font-mono">{catalog.referral.code}</strong>
                  {(() => {
                    const counts = catalog.referral.counts || {};
                    const parts = [
                      counts.WAITING ? `${counts.WAITING} cayma süresinde` : "",
                      counts.ELIGIBLE ? `${counts.ELIGIBLE} ödül hazır` : "",
                      counts.REWARDED ? `${counts.REWARDED} ödül verildi` : "",
                    ].filter(Boolean);
                    return parts.length ? <> · Davetlerin: {parts.join(", ")}</> : null;
                  })()}
                </p>
              </div>
              </div>
              <div className="flex flex-wrap gap-2 pl-7 sm:shrink-0 sm:pl-0">
                <button
                  type="button"
                  onClick={copyInviteLink}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100"
                >
                  <Copy className="h-3.5 w-3.5" />{linkCopied ? "Kopyalandı" : "Linki kopyala"}
                </button>
                <a
                  href={buildWhatsAppShareUrl(inviteText)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
                >
                  WhatsApp'ta paylaş
                </a>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="grid gap-6 lg:grid-cols-[1fr_340px]">

          {/* Sol: Paket Seçimi + Kişisel Bilgiler */}
          <div className="flex flex-col gap-6">

            {/* Erişim Paketi */}
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-base font-bold text-slate-900">Paket Seç</h2>
              <div className="flex flex-col gap-5">
                {packageGroups.map((group) => (
                  <div key={group.title || "_"}>
                    {group.title && (
                      <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">{group.title}</h3>
                    )}
                    <div className="grid gap-3 sm:grid-cols-2">
                      {group.items.map((item: any) => {
                        const selected = packageId === item.id;
                        const owned = Boolean(item.alreadyOwned);
                        const offer = installmentsSupported ? getInstallmentOffer(item) : null;
                        const features: string[] = item.features || [];
                        const opens: any[] = item.opens || [];
                        const earlyEnds = opens.filter((open) => open.endsAt);
                        return (
                          <button
                            key={item.id}
                            type="button"
                            disabled={owned}
                            aria-pressed={selected}
                            onClick={() => setPackageId(item.id)}
                            className={`flex min-w-0 flex-col gap-1.5 rounded-2xl border p-4 text-left transition ${group.items.length === 1 ? "sm:col-span-2" : ""} ${
                              selected
                                ? "border-violet-500 bg-violet-50 ring-2 ring-violet-100"
                                : owned
                                  ? "cursor-not-allowed border-slate-200 bg-slate-50 opacity-70"
                                  : "border-slate-200 hover:border-violet-300 hover:bg-slate-50"
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <span className="min-w-0 text-sm font-semibold text-slate-800">{item.label}</span>
                              {owned ? (
                                <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700">Aktif</span>
                              ) : item.recommended ? (
                                <span className="shrink-0 rounded-full bg-violet-600 px-2 py-0.5 text-[11px] font-bold text-white">Önerilen</span>
                              ) : null}
                            </div>
                            <div className="text-lg font-bold text-violet-700">{formatTry(item.price)}</div>
                            {offer && (
                              <div className={`text-[11px] font-semibold ${offer.interestFree ? "text-emerald-700" : "text-slate-600"}`}>
                                {offer.interestFree
                                  ? <>Peşin fiyatına {offer.max} taksit · {offer.max} × {formatTry(offer.perInstallment)}</>
                                  : <>{offer.max} taksite kadar (vade farkı eklenir)</>}
                              </div>
                            )}
                            {Number(item.bridgePreview) > 0 && !owned && (
                              <div className="text-[11px] font-semibold text-emerald-700">
                                Son 30 günde ödediğin {formatTry(item.bridgePreview)} bu paketten düşülür
                              </div>
                            )}
                            {item.durationType === "credits" && (
                              <div className="text-[11px] font-semibold text-slate-600">
                                {item.entryCount} giriş · {item.value} gün geçerli · günde bir giriş düşer
                              </div>
                            )}
                            {item.qrExamDaysOnly && (
                              <div className="text-[11px] font-semibold text-slate-600">
                                Salon deneme günlerinde bütün gün açık
                                {nextExamDay && <> · sonraki: {formatExamDay(nextExamDay)}</>}
                              </div>
                            )}
                            {features.length > 0 ? (
                              <ul className="flex flex-col gap-0.5 text-xs text-slate-600">
                                {features.map((line) => (
                                  <li key={line} className="flex gap-1.5">
                                    <Check className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600" />
                                    <span className="min-w-0">{line}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : opens.length > 0 && (
                              <div className="text-xs text-slate-600">Açar: {opens.map((open) => open.label).join(", ")}</div>
                            )}
                            {item.endsAtPreview && (
                              <div className="mt-auto border-t border-dashed border-slate-200 pt-1.5 text-[11px] font-medium text-slate-500">
                                Bitiş: {formatPackageEnd(item)}
                                {earlyEnds.map((open) => (
                                  <span key={open.id} className="block">{open.label}: {formatDay(open.endsAt)}</span>
                                ))}
                                {item.extendsCurrent && !owned && <span className="block">Mevcut paketinin üzerine eklenir.</span>}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Kişisel Bilgiler */}
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="mb-5 text-base font-bold text-slate-900">Kişisel Bilgiler</h2>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">Ad</label>
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={handleField("firstName")}
                    onBlur={handleBlur("firstName")}
                    placeholder="Adınız"
                    className={`w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 ${
                      formErrors.firstName ? "border-red-400 bg-red-50" : "border-slate-200 bg-slate-50"
                    }`}
                  />
                  {formErrors.firstName && <p className="mt-1 text-xs text-red-500">{formErrors.firstName}</p>}
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">Soyad</label>
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={handleField("lastName")}
                    onBlur={handleBlur("lastName")}
                    placeholder="Soyadınız"
                    className={`w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 ${
                      formErrors.lastName ? "border-red-400 bg-red-50" : "border-slate-200 bg-slate-50"
                    }`}
                  />
                  {formErrors.lastName && <p className="mt-1 text-xs text-red-500">{formErrors.lastName}</p>}
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">Telefon</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={handleField("phone")}
                    onBlur={handleBlur("phone")}
                    placeholder="05XXXXXXXXX"
                    className={`w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 ${
                      formErrors.phone ? "border-red-400 bg-red-50" : "border-slate-200 bg-slate-50"
                    }`}
                  />
                  {formErrors.phone && <p className="mt-1 text-xs text-red-500">{formErrors.phone}</p>}
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">E-Posta</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={handleField("email")}
                    onBlur={handleBlur("email")}
                    placeholder="ornek@eposta.com"
                    className={`w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 ${
                      formErrors.email ? "border-red-400 bg-red-50" : "border-slate-200 bg-slate-50"
                    }`}
                  />
                  {formErrors.email && <p className="mt-1 text-xs text-red-500">{formErrors.email}</p>}
                </div>
              </div>

              <div className="mt-5 rounded-2xl border border-violet-100 bg-violet-50/60 px-4 py-3.5">
                <div className="flex items-start gap-3">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-violet-500" />
                  <p className="text-xs leading-relaxed text-slate-600">
                    {selectedOffer
                      ? "Taksit sayısını, kart bilgisi ve ödeme detaylarıyla birlikte güvenli ödeme sayfasında seçeceksiniz."
                      : "Kart bilgisi ve ödeme detayları güvenli ödeme sayfasında gösterilecektir."}
                    {" "}Kart bilgileriniz YES.Tools sunucularında saklanmaz.
                  </p>
                </div>
              </div>
            </div>

            {/* Fatura Bilgileri */}
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="mb-1 text-base font-bold text-slate-900">Fatura Bilgileri</h2>
              <p className="mb-5 text-xs text-slate-500">Faturanız bu bilgilerle düzenlenir.</p>

              <div className="mb-5 inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1">
                {([
                  { id: "individual", label: "Bireysel" },
                  { id: "corporate", label: "Kurumsal" },
                ] as const).map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, billingType: option.id }))}
                    className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                      form.billingType === option.id ? "bg-white text-violet-700 shadow-sm" : "text-slate-500"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {form.billingType === "corporate" ? (
                  <>
                    <div className="sm:col-span-2">
                      <label className="mb-1.5 block text-sm font-semibold text-slate-700">Firma Unvanı</label>
                      <input
                        type="text"
                        value={form.companyName}
                        onChange={handleField("companyName")}
                        onBlur={handleBlur("companyName")}
                        placeholder="Firma unvanı"
                        className={`w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 ${
                          formErrors.companyName ? "border-red-400 bg-red-50" : "border-slate-200 bg-slate-50"
                        }`}
                      />
                      {formErrors.companyName && <p className="mt-1 text-xs text-red-500">{formErrors.companyName}</p>}
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-semibold text-slate-700">Vergi Dairesi</label>
                      <input
                        type="text"
                        value={form.taxOffice}
                        onChange={handleField("taxOffice")}
                        onBlur={handleBlur("taxOffice")}
                        placeholder="Vergi dairesi"
                        className={`w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 ${
                          formErrors.taxOffice ? "border-red-400 bg-red-50" : "border-slate-200 bg-slate-50"
                        }`}
                      />
                      {formErrors.taxOffice && <p className="mt-1 text-xs text-red-500">{formErrors.taxOffice}</p>}
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-semibold text-slate-700">Vergi Numarası</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={form.taxNumber}
                        onChange={handleField("taxNumber")}
                        onBlur={handleBlur("taxNumber")}
                        placeholder="10 haneli"
                        className={`w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 ${
                          formErrors.taxNumber ? "border-red-400 bg-red-50" : "border-slate-200 bg-slate-50"
                        }`}
                      />
                      {formErrors.taxNumber && <p className="mt-1 text-xs text-red-500">{formErrors.taxNumber}</p>}
                    </div>
                  </>
                ) : (
                  <div className="sm:col-span-2">
                    <label className="mb-1.5 block text-sm font-semibold text-slate-700">TC Kimlik No</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={form.identityNumber}
                      onChange={handleField("identityNumber")}
                      onBlur={handleBlur("identityNumber")}
                      placeholder="11 haneli"
                      className={`w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 ${
                        formErrors.identityNumber ? "border-red-400 bg-red-50" : "border-slate-200 bg-slate-50"
                      }`}
                    />
                    {formErrors.identityNumber && <p className="mt-1 text-xs text-red-500">{formErrors.identityNumber}</p>}
                  </div>
                )}

                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">Fatura Adresi</label>
                  <textarea
                    value={form.address}
                    onChange={(e) => {
                      const value = e.target.value;
                      setForm((prev) => ({ ...prev, address: value }));
                      if (touched.address) setFormErrors((prev) => ({ ...prev, address: validateForm({ ...form, address: value }).address }));
                    }}
                    onBlur={handleBlur("address")}
                    rows={2}
                    placeholder="Mahalle, cadde, sokak, no, daire"
                    className={`w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 ${
                      formErrors.address ? "border-red-400 bg-red-50" : "border-slate-200 bg-slate-50"
                    }`}
                  />
                  {formErrors.address && <p className="mt-1 text-xs text-red-500">{formErrors.address}</p>}
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">İlçe</label>
                  <input
                    type="text"
                    value={form.district}
                    onChange={handleField("district")}
                    onBlur={handleBlur("district")}
                    placeholder="İlçe"
                    className={`w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 ${
                      formErrors.district ? "border-red-400 bg-red-50" : "border-slate-200 bg-slate-50"
                    }`}
                  />
                  {formErrors.district && <p className="mt-1 text-xs text-red-500">{formErrors.district}</p>}
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">İl</label>
                  <input
                    type="text"
                    value={form.city}
                    onChange={handleField("city")}
                    onBlur={handleBlur("city")}
                    placeholder="İl"
                    className={`w-full rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 ${
                      formErrors.city ? "border-red-400 bg-red-50" : "border-slate-200 bg-slate-50"
                    }`}
                  />
                  {formErrors.city && <p className="mt-1 text-xs text-red-500">{formErrors.city}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Sağ: Sağlayıcı + Özet + CTA */}
          <div className="flex flex-col gap-4 lg:sticky lg:top-6 lg:self-start">

            {/* Ödeme Sağlayıcısı */}
            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="mb-4 text-sm font-bold text-slate-900">Ödeme Sağlayıcısı</h2>
              <div className="grid grid-cols-2 gap-2.5">
                {catalog.providers.map((item: any) => {
                  const meta = getPaymentProviderMeta(item.id);
                  const selected = provider === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setProvider(item.id)}
                      className={`flex flex-col items-center justify-center gap-2 rounded-2xl border px-3 py-3.5 transition ${
                        selected
                          ? "border-violet-400 bg-violet-50 ring-2 ring-violet-100"
                          : "border-slate-200 bg-white hover:border-violet-300 hover:bg-slate-50"
                      }`}
                    >
                      <img
                        src={meta.logo}
                        alt={meta.label}
                        className={`${meta.logoClassName} max-w-[90px] object-contain`}
                      />
                      <span className={`text-[11px] font-semibold ${selected ? "text-violet-700" : "text-slate-500"}`}>
                        ile Öde
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Ödeme Özeti + Sözleşmeler + CTA */}
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-sm font-bold text-slate-900">Ödeme Özeti</h2>
              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Paket</span>
                  <span className="font-medium text-slate-900">{selectedPackage?.label || "—"}</span>
                </div>
                {selectedPackage?.opens?.length > 0 && (
                  <div className="flex justify-between gap-3">
                    <span className="shrink-0 text-slate-500">Açtığı bölümler</span>
                    <span className="text-right font-medium text-slate-900">{selectedPackage.opens.map((open: any) => open.label).join(", ")}</span>
                  </div>
                )}
                {selectedPackage?.endsAtPreview && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">{selectedPackage.extendsCurrent ? "Yeni bitiş" : "Bitiş"}</span>
                    <span className="font-medium text-slate-900">{formatPackageEnd(selectedPackage)}</span>
                  </div>
                )}
                {selectedOffer && (
                  <div className="flex justify-between gap-3">
                    <span className="text-slate-500">Taksit</span>
                    <span className={`text-right font-medium ${selectedOffer.interestFree ? "text-emerald-700" : "text-slate-900"}`}>
                      {selectedOffer.interestFree
                        ? <>Peşin fiyatına {selectedOffer.max} taksite kadar<br /><span className="text-xs font-normal text-slate-500">{selectedOffer.max} taksitte {selectedOffer.max} × {formatTry(selectedOffer.perInstallment)}</span></>
                        : <>{selectedOffer.max} taksite kadar<br /><span className="text-xs font-normal text-slate-500">vade farkı ödeme sayfasında eklenir</span></>}
                    </span>
                  </div>
                )}
                {Number(selectedPackage?.referralDiscount) > 0 && (
                  <div className="border-t border-slate-100 pt-2.5">
                    <label htmlFor="payment-referral-code" className="mb-1 block text-xs font-semibold text-slate-500">Davet kodu</label>
                    <div className="flex gap-2">
                      <input
                        id="payment-referral-code"
                        type="text"
                        value={referralInput}
                        maxLength={20}
                        placeholder="YES-XXXXX"
                        onChange={(e) => setReferralInput(e.target.value.toUpperCase())}
                        className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-violet-300"
                      />
                      <button
                        type="button"
                        onClick={() => setAppliedReferralCode(referralInput.trim())}
                        disabled={checkingDiscounts || referralInput.trim() === appliedReferralCode}
                        className="shrink-0 rounded-xl border border-violet-200 bg-violet-50 px-3 py-2 text-xs font-semibold text-violet-700 hover:bg-violet-100 disabled:opacity-50"
                      >
                        Uygula
                      </button>
                    </div>
                    {discounts?.referralMessage && <p className="mt-1 text-xs text-red-500">{discounts.referralMessage}</p>}
                    {discounts?.referral && (
                      <p className="mt-1 text-xs text-emerald-700">{discounts.referral.referrerName} davetiyle geliyorsun.</p>
                    )}
                  </div>
                )}
                {(discounts?.referral || discounts?.bridge) && (
                  <div className="space-y-1.5 border-t border-slate-100 pt-2.5">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Paket fiyatı</span>
                      <span className="font-medium text-slate-900">{formatTry(discounts.listPrice)}</span>
                    </div>
                    {discounts.referral && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Davet indirimi</span>
                        <span className="font-medium text-emerald-700">−{formatTry(discounts.referral.amount)}</span>
                      </div>
                    )}
                    {discounts.bridge && (
                      <div className="flex justify-between gap-3">
                        <span className="text-slate-500">Son 30 günde ödediklerin</span>
                        <span className="font-medium text-emerald-700">−{formatTry(discounts.bridge.amount)}</span>
                      </div>
                    )}
                  </div>
                )}
                <div className="border-t border-slate-100 pt-2.5">
                  <div className="flex items-baseline justify-between">
                    <span className="font-semibold text-slate-900">Toplam</span>
                    <span className="text-2xl font-bold text-violet-700">
                      {checkingDiscounts ? <Loader2 className="h-5 w-5 animate-spin" /> : formatTry(payable)}
                    </span>
                  </div>
                </div>
                <p className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-xs leading-relaxed text-slate-600">
                  Tüm ödemeler online olarak kredi kartı veya banka kartıyla alınır. Elden ödeme ve elden taksit yapılmaz.
                  Taksit seçenekleri bankanız üzerinden, peşin fiyatına sunulur.
                </p>
              </div>

              {/* Sözleşmeler */}
              <OnBilgilendirmePenceresi
                acik={onBilgiAcik}
                kapat={() => setOnBilgiAcik(false)}
                onayla={() => { setOnBilgiAccepted(true); setLegalError(""); }}
                istek={{
                  packageId,
                  referralCode: discounts?.referral ? appliedReferralCode : undefined,
                  billing: { ...form, type: form.billingType },
                }}
              />
              <div className="mt-5 space-y-3 border-t border-slate-100 pt-5">
                <label className="flex cursor-pointer items-start gap-2.5 text-xs text-slate-700">
                  <input
                    type="checkbox"
                    checked={onBilgiAccepted}
                    onChange={(e) => { if (e.target.checked) setOnBilgiAcik(true); else setOnBilgiAccepted(false); setLegalError(""); }}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded accent-violet-600"
                  />
                  <span className="leading-relaxed">
                    <button type="button" onClick={() => setOnBilgiAcik(true)} className="font-semibold text-violet-700 underline underline-offset-2">Ön Bilgilendirme Formu</button>
                    'nu okudum ve onaylıyorum.
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-2.5 text-xs text-slate-700">
                  <input
                    type="checkbox"
                    checked={privacyAccepted}
                    onChange={(e) => { setPrivacyAccepted(e.target.checked); setLegalError(""); }}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded accent-violet-600"
                  />
                  <span className="leading-relaxed">
                    <a href="https://yeniegitimsistemi.net/gizlilik-politikasi" target="_blank" rel="noopener noreferrer" className="font-semibold text-violet-700 underline underline-offset-2">Gizlilik Politikası</a>
                    {" "}ve{" "}
                    <a href="https://yeniegitimsistemi.net/kullanici-sozlesmesi" target="_blank" rel="noopener noreferrer" className="font-semibold text-violet-700 underline underline-offset-2">Kullanıcı Sözleşmesi</a>
                    {" "}metinlerini okudum ve kabul ediyorum.
                  </span>
                </label>

                <label className="flex cursor-pointer items-start gap-2.5 text-xs text-slate-700">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => { setTermsAccepted(e.target.checked); setLegalError(""); }}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded accent-violet-600"
                  />
                  <span className="leading-relaxed">
                    <a href="https://yeniegitimsistemi.net/mesafeli-satis-sozlesmesi" target="_blank" rel="noopener noreferrer" className="font-semibold text-violet-700 underline underline-offset-2">Mesafeli Satış Sözleşmesi</a>
                    {" "}ile{" "}
                    <a href="https://yeniegitimsistemi.net/islem-rehberi-teslimat-iptal-iade-kosullari" target="_blank" rel="noopener noreferrer" className="font-semibold text-violet-700 underline underline-offset-2">İşlem Rehberi, Teslimat, İptal ve İade Koşulları</a>
                    {" "}metinlerini okudum ve kabul ediyorum.
                  </span>
                </label>

                {legalError && <p className="text-xs text-red-500">{legalError}</p>}
              </div>

              {error && (
                <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={!packageId || !provider || submitting || Boolean(selectedPackage?.alreadyOwned)}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-violet-600 px-4 py-3.5 text-sm font-semibold text-white transition hover:bg-violet-700 disabled:opacity-50"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                {submitting ? "Yönlendiriliyor..." : "Güvenli Ödemeye Geç"}
              </button>
              <p className="mt-2 text-center text-xs text-slate-500">Siparişi onaylayarak ödeme yükümlülüğü altına girdiğinizi kabul edersiniz.</p>

              {/* Güvenli ödeme notu */}
              {selectedProviderMeta && (
                <p className="mt-3 flex flex-wrap items-center justify-center gap-1.5 text-center text-xs leading-relaxed text-slate-400">
                  Ödeme işlemleri
                  <img
                    src={selectedProviderMeta.secureLogo || selectedProviderMeta.logo}
                    alt={selectedProviderMeta.label}
                    className="inline-block h-4 object-contain"
                  />
                  altyapısı ile güvenli şekilde gerçekleştirilir.
                </p>
              )}
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="mt-10 border-t border-slate-200 pt-8">
          <div className="mb-5 flex flex-wrap items-center justify-center gap-3">
            {catalog.providers.map((item: any) => {
              const meta = getPaymentProviderMeta(item.id);
              const src = meta.secureLogo || meta.logo;
              const cls = meta.secureLogoClassName || meta.logoClassName;
              return (
                <div key={item.id} className="flex h-12 w-36 items-center justify-center rounded-xl border border-slate-100 bg-white px-3 opacity-60 grayscale transition hover:opacity-100 hover:grayscale-0">
                  <img src={src} alt={meta.label} className={`${cls} max-w-full object-contain`} />
                </div>
              );
            })}
          </div>

          <div className="mb-6 flex flex-wrap items-center justify-center gap-3">
            {CARD_LOGOS.map((card) => (
              <div key={card.name} className="flex h-10 w-16 items-center justify-center rounded-lg border border-slate-100 bg-white px-2 opacity-60 grayscale transition hover:opacity-100 hover:grayscale-0">
                <img src={card.src} alt={card.name} className={`${card.className} max-w-full object-contain`} />
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-slate-400">
            © {new Date().getFullYear()} YES.Tools. Tüm hakları saklıdır.
          </p>
        </div>
      </div>
    </div>
  );
}
